import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { AnimatePresence, motion } from 'motion/react';
import { Icon } from '@frontend/icons';
import { isRefObject } from '@frontend/types';
import { theme } from '@frontend/theme';
import { KeyNames, useModalControl, useTabbable } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR, EVENT_BACKGROUND_COLOR } from '../../../constants';
import { useGetAppointmentDetails } from '../../../hooks';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { getDateString } from '../../../utils/date-helpers';
import { OutOfOfficeEventCardModal } from '../../modals/out-of-office-event-card-modal';
import { PersonStatusIndicator } from '../../schedule-calendar-components/appointment-event-card/person-status-indicator';
import { useCalendarView, useCalendarViewProps } from '../providers';
import { EventType } from '../types';
import {
  checkIsNonAttendedEventStatus,
  EVENT_CARD_DEFAULT_MEASUREMENTS,
  getDistanceFromParentEdges,
  getDurationFromHours,
  getEventCardCssForVerticalView,
  maxTotalGridColumns,
  PX_PER_MINUTE,
  zIndex,
} from '../utils';
import { getEventPlacementValues, getEventStatusIconName } from './helper';
import { getOverlappingEventIndicatorVariant } from './overlapping-event-indicator';

type EventCardProps = {
  id: string;
  locationName: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  title: string;
  startDate: string;
  calendarDate?: string;
  startHour: string;
  endHour: string;
  appointmentType?: string;
  eventTypeColor?: string;
  disabled?: boolean;
  laneCount?: number;
  laneIndex?: number;
  type: EventType;
  visibleLaneCount: number;
  personStatus?: string;
  eventStatus?: EventStatus;
  backgroundColor?: string; // HEX color
};

const EXPANDED_CARD_DEFAULT_HEIGHT = 248;

export const CalendarEventCardV3 = forwardRef(
  (
    {
      id,
      title,
      type,
      locationName,
      locationId,
      entityId,
      entityName,
      startDate,
      calendarDate,
      startHour,
      endHour,
      eventTypeColor,
      disabled = false,
      laneCount = 0,
      laneIndex = 0,
      visibleLaneCount,
      personStatus,
      eventStatus,
      appointmentType,
      backgroundColor = EVENT_BACKGROUND_COLOR,
    }: EventCardProps,
    ref: React.Ref<HTMLElement>
  ) => {
    const [isEventCardExpanded, setEventCardExpanded] = useState(false);
    const [isTapped, setIsTapped] = useState(false);

    const { startHour: calendarStartHour, endHour: calendarEndHour, EventCardComponent } = useCalendarViewProps();
    const { setBackgroundView, setFloatingTooltipData } = useCalendarView([
      'setBackgroundView',
      'setFloatingTooltipData',
    ]);

    const eventCardWrapperRef = useRef<HTMLDivElement | null>(null);
    const eventCardContentWrapperRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef(EXPANDED_CARD_DEFAULT_HEIGHT);

    const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
    const isAppointment = type === 'appointment';
    const primaryColor = eventTypeColor ?? (isAppointment ? PRIMARY_EVENT_COLOR : theme.colors.neutral50);

    const isNonAttendedEvent = checkIsNonAttendedEventStatus(eventStatus);
    const eventStatusIconName = getEventStatusIconName(eventStatus);

    const { triggerProps: outOfOfficeTriggerProps, modalProps: outOfOfficeModalProps } = useModalControl({
      onClose: () => collapseEventCard(),
    });
    const { triggerProps: viewAppointmentTriggerProps, Modal: AppointmentDetailsModal } = useGetAppointmentDetails({
      appointmentId: id,
      locationId: locationId ?? '',
      eventColor: primaryColor,
      onModalClose: () => {
        collapseEventCard();
      },
      enabled: isAppointment,
    });

    const expandEventCard = () => {
      if (isAppointment) {
        viewAppointmentTriggerProps.onClick();
      } else {
        outOfOfficeTriggerProps.onClick();
      }

      if (!(isRefObject(ref) && eventCardWrapperRef.current && ref.current)) return;
      if (!disabled) setEventCardExpanded(true);
      setBackgroundView(true);
    };

    const collapseEventCard = () => {
      setEventCardExpanded(false);
      setBackgroundView(false);
      setFloatingTooltipData(undefined);
      setIsTapped(false);
    };

    useTabbable({ ref: eventCardContentWrapperRef, autofocus: 'first' });

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === KeyNames.Enter || e.key === KeyNames.Space) && !isEventCardExpanded) {
        e.preventDefault();
        expandEventCard();
        eventCardContentWrapperRef.current?.focus();
      }
      if (e.key === KeyNames.Escape && isEventCardExpanded) {
        e.preventDefault();
        collapseEventCard();
      }
    };

    const shouldOpenUp = () => {
      if (!(isRefObject(ref) && eventCardWrapperRef.current && ref.current)) return false;

      return (
        getDistanceFromParentEdges(eventCardWrapperRef.current, ref.current).bottom < measureRef.current + 10 //measureRef is the measured height of the container + 10 offset
      );
    };

    const placementValues = getEventPlacementValues({
      calendarDate: calendarDate ?? '',
      // for exceptions we were not getting startDate so need to use calendar date
      // TODO: we need to update this once we handle exceptions correctly for overlapping cases
      startDate: startDate ?? getDateString(calendarDate) ?? '',
      startHour,
      endHour,
    });

    const shouldHideEventCard = () => {
      const maxTotalMinutes = maxTotalGridColumns(calendarStartHour, calendarEndHour);

      return !(
        (placementValues.startGridColumn <= 1 && placementValues.endGridColumn <= 1) ||
        (placementValues.startGridColumn === maxTotalMinutes && placementValues.endGridColumn === maxTotalMinutes) ||
        // hide event card if it's outside of the visible lane count
        laneIndex >= visibleLaneCount
      );
    };

    const overlappingEventIndicatorVariant = useMemo(
      () =>
        placementValues.isOverlappingEvent
          ? getOverlappingEventIndicatorVariant(placementValues.totalDuration)
          : undefined,
      [placementValues.totalDuration, placementValues.isOverlappingEvent]
    );

    // Reset background view when the component unmounts
    useEffect(() => {
      return () => setBackgroundView(false);
    }, [setBackgroundView]);

    return (
      <motion.div
        data-event-id={id} // use to focus event card in onboarding-flow
        data-event='true'
        tabIndex={isEventCardExpanded ? -1 : 0}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setFloatingTooltipData({ name: title, value: duration, appointmentType: appointmentType })}
        onMouseLeave={() => setFloatingTooltipData(undefined)}
        onTouchEnd={(event) => {
          // If the event card with non attended status is tapped first time, show the text on card preview
          // and open the card modal on second tap
          if (!isTapped && checkIsNonAttendedEventStatus(eventStatus)) {
            event.preventDefault();
            setIsTapped(true);
          } else if (!isEventCardExpanded) {
            event.preventDefault();
            expandEventCard();
          }
        }}
        ref={eventCardWrapperRef}
        layout
        css={[
          !shouldHideEventCard() &&
            css`
              display: none;
            `,
          css`
            position: absolute;
            top: ${placementValues.startGridColumn}px;
            height: ${(placementValues.totalDuration - 1) * PX_PER_MINUTE}px;
            transition: left 0.5s ease-in-out, width 0.5s ease-in-out;
          `,
          getEventCardCssForVerticalView(visibleLaneCount, laneCount, laneIndex),
          css`
            user-select: none;
            outline: none;
            &::after {
              content: '';
              width: calc(100% + 8px);
              height: 100%;
              position: absolute;
              left: 50%;
              top: 50%;
              pointer-events: none;
              border-radius: ${theme.borderRadius.medium};
              opacity: 0;
              transition: 0.2s ease;
              transform: translate(-50%, -50%);
              border: 2px solid ${theme.colors.primary30};
            }
            &:focus-visible {
              &::after {
                opacity: 1;
              }
            }
          `,
          isNonAttendedEvent && nonAttendedEventStyle(isEventCardExpanded, isTapped),
          !isEventCardExpanded &&
            css`
              cursor: pointer;
            `,
        ]}
        style={{ zIndex: zIndex.eventCardDefault }}
      >
        {isNonAttendedEvent && eventStatusIconName && (
          <Icon
            name={eventStatusIconName}
            color='error'
            size={16}
            css={{
              position: 'absolute',
              top: theme.spacing(0.5),
              right: theme.spacing(0.5),
              transition: 'opacity 0.2s ease-in-out',
            }}
          />
        )}
        <AnimatePresence>
          {isEventCardExpanded && <EventDurationLine shouldOpenLeft={!isAppointment} eventColor={primaryColor} />}
        </AnimatePresence>
        <motion.article
          data-event-id={id}
          tabIndex={isEventCardExpanded ? 0 : -1}
          onKeyDown={handleKeyDown}
          data-trackingid={
            isAppointment ? CALENDAR_TRACKING_IDS.appointmentEventCard : CALENDAR_TRACKING_IDS.outOfOfficeEventCard
          }
          ref={eventCardContentWrapperRef}
          onMouseDown={(e) => e.stopPropagation()}
          layout={!isAppointment}
          onClick={() => {
            if (!isEventCardExpanded) expandEventCard();
          }}
          onLayoutMeasure={(box) => {
            if (!isAppointment && isEventCardExpanded && box) {
              measureRef.current = box.y.max - box.y.min;
            }
          }}
          style={{
            borderRadius: theme.borderRadius.small,
            padding: theme.spacing(0.5, 0.5, 0, 1.5),
            ...(isEventCardExpanded &&
              !isAppointment &&
              shouldOpenUp() && { marginTop: -measureRef.current + EVENT_CARD_DEFAULT_MEASUREMENTS.condensedHeight }),
          }}
          css={[
            css`
              box-shadow: ${theme.shadows.light};
              position: relative;
              overflow: hidden;
              outline: none;
              background: ${isNonAttendedEvent
                ? 'transparent'
                : isAppointment
                ? backgroundColor
                : theme.colors.neutral20};
              height: 100%;
              transition: box-shadow 0.2s ease-in-out, scale 0.2s ease-in-out;
              &:hover {
                box-shadow: ${theme.shadows.floating};
                scale: ${isNonAttendedEvent ? 1 : 1.01};
              }
            `,
            isNonAttendedEvent &&
              css`
                opacity: ${isEventCardExpanded ? 0.5 : 0};
                transition: opacity 0.3s ease-in-out;
              `,
          ]}
        >
          <PersonStatusIndicator personStatus={personStatus} height={4} borderRadius='small' isExpanded={false} />

          <EventColoredLine eventColor={primaryColor} expanded={isEventCardExpanded && !isAppointment} />
          {/* remove this condition to allow modal to animate */}
          {AppointmentDetailsModal}
          <OutOfOfficeEventCardModal
            id={id}
            eventType={type}
            title={title}
            duration={duration}
            locationId={locationId}
            locationName={locationName}
            providerId={entityId}
            startDate={startDate}
            providerName={entityName}
            calendarDate={calendarDate}
            modalProps={outOfOfficeModalProps}
          />
          <EventCardComponent
            id={id}
            eventType={type}
            title={title}
            duration={duration}
            locationName={locationName}
            locationId={locationId}
            providerId={entityId}
            providerName={entityName}
            isExpanded={isEventCardExpanded}
            startDate={startDate}
            isYesterdayEvent={placementValues.isYesterdayEvent}
            overlappingIndicatorVariant={overlappingEventIndicatorVariant}
          />
        </motion.article>
      </motion.div>
    );
  }
);

CalendarEventCardV3.displayName = 'CalendarEventCardV3';

type EventDurationLineProps = {
  eventColor: string;
  shouldOpenLeft?: boolean;
};

const EventDurationLine = ({ eventColor, shouldOpenLeft = false }: EventDurationLineProps) => {
  return (
    <motion.div
      initial='hidden'
      animate='visible'
      exit='hidden'
      variants={{
        hidden: {
          height: 0,
          opacity: 0,
        },
        visible: {
          height: '100%',
          opacity: 1,
        },
      }}
      transition={{ duration: 0.3 }}
      css={[
        css`
          position: absolute;
          width: 100%;
          height: 2px;
          background: ${eventColor};
        `,
        !shouldOpenLeft &&
          css`
            width: 2px;
            height: 100%;
            right: -10px;
          `,
        shouldOpenLeft &&
          css`
            width: 2px;
            height: 100%;
            left: -10px;
          `,
      ]}
    >
      <motion.div
        css={[
          css`
            position: absolute;
            height: 10px;
            width: 10px;
            border-radius: 30px;
            background: ${eventColor};
            left: 50%;
            transform: translateX(-50%);
            bottom: 0;
          `,
        ]}
      />
    </motion.div>
  );
};

const EventColoredLine = ({ eventColor, expanded }: { eventColor: string; expanded: boolean }) => {
  return (
    <motion.div
      layout='preserve-aspect'
      style={{
        position: 'absolute',
        background: eventColor,
        left: 0,
        top: 0,
        height: '200%',
        width: theme.spacing(0.5),
        borderRadius: `${theme.borderRadius.small} 0 0 ${theme.borderRadius.small}`,
      }}
      transition={{ duration: 0.3, ease: 'linear' }}
      animate={{
        width: theme.spacing(expanded ? 1 : 0.5),
      }}
    />
  );
};

const nonAttendedCardHoverStyle = css`
  background: ${theme.colors.white};
  border-color: transparent;
  & > article {
    opacity: 0.5;
  }
  & > svg {
    opacity: 0;
  }
`;

const nonAttendedEventStyle = (isEventCardExpanded: boolean, isTapped: boolean) => css`
  background: ${theme.colors.critical20}33; // with alpha for 20% opacity
  border: 1px dashed ${theme.colors.critical50};
  border-radius: ${theme.borderRadius.small};
  transition: all 0.3s cubic-bezier(0.7, -0.4, 0.4, 1.4);

  &:hover,
  &:focus-visible {
    ${nonAttendedCardHoverStyle}
  }

  ${isTapped && nonAttendedCardHoverStyle}

  ${isEventCardExpanded
    ? css`
        background: ${theme.colors.white};
        border-color: transparent;
      `
    : ''}
`;
